import { useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "../context/TenantContext.jsx";
import { resolveActiveTenant } from "../utils/companyIdentifier.js";

/**
 * Custom hook for tenant-aware navigation
 * Automatically prepends tenant name to routes when in tenant context
 *
 * Usage:
 * const navigate = useTenantNavigation();
 * navigate("/app/dashboard"); // Automatically becomes "/:tenantName/app/dashboard" if in tenant context
 */
export const useTenantNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantName, hasTenant } = useTenant();

  return (path, options = {}) => {
    // Handle numeric navigation (e.g., -1 for back, 1 for forward)
    if (typeof path === "number") {
      navigate(path);
      return;
    }

    // Context, then the current URL, then the identifier stored at login
    const activeTenant = resolveActiveTenant({ tenantName, hasTenant, pathname: location.pathname, targetPath: path });

    // If we have an active tenant and path doesn't already have tenant prefix
    if (activeTenant && !path.startsWith(`/${activeTenant}`)) {
      // Ensure path starts with /
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      // Prepend tenant name
      const tenantPath = `/${activeTenant}${normalizedPath}`;
      navigate(tenantPath, options);
    } else {
      // No tenant context, navigate normally
      navigate(path, options);
    }
  };
};

/**
 * Hook to build tenant-aware URLs
 * Use this when you need to construct URLs without navigating
 *
 * Usage:
 * const buildUrl = useTenantUrl();
 * const url = buildUrl("/app/dashboard"); // Returns "/:tenantName/app/dashboard" if in tenant context
 */
export const useTenantUrl = () => {
  const location = useLocation();
  const { tenantName, hasTenant } = useTenant();

  return (path) => {
    const activeTenant = resolveActiveTenant({ tenantName, hasTenant, pathname: location.pathname, targetPath: path });
    if (activeTenant && !path.startsWith(`/${activeTenant}`)) {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return `/${activeTenant}${normalizedPath}`;
    }
    return path;
  };
};
